import { h } from 'preact'
import { useEffect, useMemo } from 'preact/hooks'
import { ValueType } from '../../models/patient'
import cx from '../../shared/classes'
import randomString from '../../shared/random-string'
import { CustomFieldType } from './custom-field-types'
import ButtonGroupSelector, { Option } from './button-group-selector'
import RadioButtonGroup from './radiobutton-group'
import DatePicker from './date-picker'
import SearchSelect, { Item } from './search-select'
import * as style from './style.css'

interface Props {
    customFieldType?: CustomFieldType
    value?: ValueType
    required?: boolean
    inputType?: string
    options?: Option[]
    placeholder?: string
    label?: string
    error?: string
    disabled?: boolean
    autoFocus?: boolean
    isDirty?: boolean
    isFocused?: boolean
    inputProps?: {
        maxLength?: number
        type?: string
        autocomplete?: string
    }
    onFocus?: (field?: string) => void
    onChange?: (value?: ValueType) => void
    onBlur?: (value?: ValueType) => void
    hidePicker?: boolean
    customLabelClass?: string
    className?: string
    wrapperClassName?: string
    tooltip?: string
    items?: Item[]
}

type ReferenceType = HTMLInputElement | HTMLSelectElement | null

const CustomField: preact.FunctionalComponent<Props> = (props) => {
    const {
        children,
        error,
        disabled,
        label,
        autoFocus,
        value,
        placeholder,
        required,
        customFieldType,
        inputType,
        options,
        isDirty,
        isFocused,
        inputProps,
        onFocus,
        onChange,
        onBlur,
        hidePicker,
        customLabelClass,
        className = '',
        wrapperClassName = '',
        tooltip,
        items,
    } = props
    let customField: ReferenceType = null
    const uniqId = useMemo(() => randomString(), [])

    useEffect(() => {
        if (autoFocus && customField) {
            customField.focus()
        }
    }, [autoFocus, customField])

    const setElementReference = (ref: ReferenceType) => {
        customField = ref
    }

    const handleFocus = () => onFocus !== undefined && onFocus()

    const renderCorrectCustomField = () => {
        const commonProps = {
            id: uniqId,
            disabled,
            placeholder,
            required,
        }

        switch (customFieldType) {
            case CustomFieldType.TEXTAREA:
                return (
                    <textarea
                        {...commonProps}
                        {...inputProps}
                        class={`${style.customTextarea} ${className}`}
                        onChange={(event: Event) => {
                            const { value: targetValue } = event.target as HTMLTextAreaElement
                            return onChange && onChange(targetValue)
                        }}
                        onFocus={handleFocus}
                    >
                        {value as string}
                    </textarea>
                )
            case CustomFieldType.SELECT:
                return (
                    <select
                        {...commonProps}
                        value={value as string}
                        class={`${style.customSelect} ${className}`}
                        onChange={(event: Event) => {
                            const { value: targetValue } = event.target as HTMLSelectElement
                            return onChange && onChange(targetValue)
                        }}
                        onFocus={handleFocus}
                        ref={setElementReference}
                    >
                        {children}
                    </select>
                )
            case CustomFieldType.SEARCH_SELECT:
                return (
                    <SearchSelect
                        {...commonProps}
                        value={value as string}
                        onChange={(value: string) => {
                            return onChange && onChange(value)
                        }}
                        items={items ?? []}
                        keyProperty="name"
                        displayProperty="name"
                        search={true}
                    />
                )
            case CustomFieldType.BUTTON_GROUP:
                return (
                    <ButtonGroupSelector
                        {...commonProps}
                        value={value as string}
                        items={options || []}
                        onSelect={(targetValue?: string) => {
                            handleFocus()
                            return onChange && onChange(targetValue)
                        }}
                    />
                )
            case CustomFieldType.RADIOBUTTON_GROUP:
                return (
                    <RadioButtonGroup
                        {...commonProps}
                        value={value as string}
                        items={options || []}
                        onSelect={(targetValue?: string) => {
                            handleFocus()
                            return onChange && onChange(targetValue)
                        }}
                    />
                )
            case CustomFieldType.DATEPICKER:
                return (
                    <DatePicker
                        {...commonProps}
                        value={value as string}
                        onFocus={handleFocus}
                        isFocused={isFocused}
                        hidePicker={hidePicker}
                        onChange={(day: string) => {
                            handleFocus()
                            return onChange && onChange(day)
                        }}
                    />
                )
            default:
                return (
                    <input
                        {...commonProps}
                        {...inputProps}
                        ref={setElementReference}
                        value={value as string}
                        type={inputType}
                        class={`${style.customInput} ${className}`}
                        onFocus={handleFocus}
                        onBlur={(event: Event) => {
                            const { value: targetValue } = event.target as HTMLInputElement
                            if (onBlur) {
                                return onBlur(targetValue)
                            }
                            return onChange && onChange(targetValue)
                        }}
                        onChange={(event: Event) => {
                            if (isDirty) {
                                const { value: targetValue } = event.target as HTMLInputElement
                                return onChange && onChange(targetValue)
                            }
                        }}
                    />
                )
        }
    }

    return (
        <div class={`${style.customField} ${wrapperClassName}`}>
            <div
                class={cx({
                    [style.field]: true,
                    [style.fieldDisabled]: disabled,
                    [wrapperClassName]: Boolean(wrapperClassName),
                })}
            >
                {Boolean(label) && (
                    <label htmlFor={uniqId} class={`${style.label} ${customLabelClass || ''}`}>
                        {label}
                        {tooltip && (
                            <span class={style.tooltip}>
                                <span class={style.tooltipIcon}>i</span>
                                <span class={style.tooltipMessage}>{tooltip}</span>
                            </span>
                        )}
                    </label>
                )}

                {renderCorrectCustomField()}

                {Boolean(error) && <span class={style.errorMessage}>{error}</span>}
            </div>
        </div>
    )
}

export default CustomField
